package rendering

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.spring
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.launch

/**
 * Manages board shake animation state.
 *
 * Call [triggerShake] to start a shake with a given intensity.
 * Read [offsetX] and [offsetY] to get the current translation to apply via graphicsLayer.
 */
class ShakeState {
    /** Animatable X translation for the shake effect. */
    val offsetX = Animatable(0f)

    /** Animatable Y translation for the shake effect. */
    val offsetY = Animatable(0f)

    /**
     * Triggers a shake animation with the given intensity.
     * Intensity 1.0 = full shake (~12px), 0.0 = no shake.
     * Uses a spring animation that decays back to zero.
     *
     * @param scope     CoroutineScope to launch the animation in.
     * @param intensity Shake intensity in [0.0, 1.0].
     */
    fun triggerShake(scope: CoroutineScope, intensity: Float) {
        if (intensity <= 0f) return
        val maxOffset = 12f * intensity
        scope.launch {
            // Snap to initial offset
            offsetX.snapTo(maxOffset)
            offsetY.snapTo(maxOffset * 0.5f)
            // Spring back to zero
            launch { offsetX.animateTo(0f, spring(dampingRatio = 0.3f, stiffness = 200f)) }
            launch { offsetY.animateTo(0f, spring(dampingRatio = 0.3f, stiffness = 200f)) }
        }
    }
}
